/**
 * Newsletter sign-up form (CDN-2529).
 *
 * Handles every [data-tvk-newsletter] form on the page (footer + blocks).
 * Markup: inc/template-parts/newsletter/form.php. Contract:
 * docs/design-system/v1-betting/sessions/41-newsletter-signup.md.
 *
 *   - `started_at` (unix seconds) is set on the first focusin of the form. It
 *     is never in the cached HTML.
 *   - Client checks run before any request: a valid email and the required
 *     newsletter box. A failed check marks the field `aria-invalid`, shows its
 *     error text and moves focus to the first invalid field.
 *   - POST JSON to `data-endpoint`. Reply `{ok:true}` = success; `{ok:false,
 *     code}` or any other answer maps to a text from `data-messages`. A request
 *     that takes longer than TIMEOUT_MS is aborted and counts as `error`.
 *   - Success hides the fields, shows the message in the status region and
 *     pushes `{event: 'newsletter_signup', placement}` to the dataLayer. No
 *     email or other form data goes to the dataLayer.
 *
 * Texts come from PHP (`data-messages`, English msgids translated per locale).
 */
(function () {
	"use strict";

	var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
	var CODES = ["invalid_email", "consent_required", "rate_limited", "error"];
	var TIMEOUT_MS = 15000;

	function readMessages(form) {
		try {
			var parsed = JSON.parse(form.getAttribute("data-messages") || "{}");
			return parsed && typeof parsed === "object" ? parsed : {};
		} catch (e) {
			return {};
		}
	}

	function errorFor(field) {
		var id = field.getAttribute("aria-describedby");
		return id ? document.getElementById(id) : null;
	}

	function setInvalid(field, message) {
		var error = errorFor(field);
		field.setAttribute("aria-invalid", "true");
		if (error) {
			error.textContent = message;
			error.hidden = false;
		}
	}

	function clearInvalid(field) {
		var error = errorFor(field);
		field.removeAttribute("aria-invalid");
		if (error) {
			error.textContent = "";
			error.hidden = true;
		}
	}

	function setStatus(status, state, message) {
		status.classList.remove("is-loading", "is-error", "is-success");
		if (state) {
			status.classList.add("is-" + state);
		}
		status.textContent = message || "";
	}

	function init(form) {
		var messages = readMessages(form);
		var email = form.querySelector('input[name="email"]');
		var newsletter = form.querySelector('input[name="newsletter"]');
		var marketing = form.querySelector('input[name="marketing"]');
		var honeypot = form.querySelector('input[name="tvk_nl_ref"]');
		var submit = form.querySelector('[type="submit"]');
		var status = form.querySelector('[role="status"]');
		var endpoint = form.getAttribute("data-endpoint") || "";
		var source = form.getAttribute("data-source") === "block" ? "block" : "footer";
		var startedAt = 0;
		var busy = false;

		if (!email || !newsletter || !submit || !status || !endpoint) {
			return;
		}

		function text(key) {
			return messages[key] || messages.error || "";
		}

		// aria-disabled, not `disabled`: a disabled button loses focus to <body>,
		// and an error would leave a keyboard user at the top of the page. The
		// `busy` guard blocks a second submit. aria-busy stays off the form: the
		// status live region is inside it, and a busy region may stay silent.
		function setBusy(on) {
			busy = on;
			if (on) {
				submit.setAttribute("aria-disabled", "true");
				submit.setAttribute("aria-busy", "true");
			} else {
				submit.removeAttribute("aria-disabled");
				submit.removeAttribute("aria-busy");
			}
		}

		function validate() {
			var invalid = [];
			var value = email.value.trim();

			if (!EMAIL_PATTERN.test(value)) {
				setInvalid(email, text("invalid_email"));
				invalid.push(email);
			} else {
				clearInvalid(email);
			}

			if (!newsletter.checked) {
				setInvalid(newsletter, text("consent_required"));
				invalid.push(newsletter);
			} else {
				clearInvalid(newsletter);
			}

			return invalid;
		}

		function fail(code) {
			setBusy(false);
			if (code === "invalid_email") {
				setStatus(status, "", "");
				setInvalid(email, text("invalid_email"));
				email.focus();
				return;
			}
			if (code === "consent_required") {
				setStatus(status, "", "");
				setInvalid(newsletter, text("consent_required"));
				newsletter.focus();
				return;
			}
			setStatus(status, "error", text(CODES.indexOf(code) > -1 ? code : "error"));
		}

		function succeed() {
			setBusy(false);
			form.classList.add("is-done");
			setStatus(status, "success", text("success"));
			status.focus();

			// A broken GTM tag can throw from push(). The sign-up is already done,
			// so the throw must not reach .catch() and show the error state.
			try {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({ event: "newsletter_signup", placement: source });
			} catch (e) {
				// Tracking only: nothing to recover.
			}
		}

		form.addEventListener("focusin", function () {
			if (!startedAt) {
				startedAt = Math.floor(Date.now() / 1000);
			}
		});

		// A fixed field loses its error as soon as it becomes valid.
		email.addEventListener("input", function () {
			if (email.getAttribute("aria-invalid") === "true" && EMAIL_PATTERN.test(email.value.trim())) {
				clearInvalid(email);
			}
		});
		newsletter.addEventListener("change", function () {
			if (newsletter.checked) {
				clearInvalid(newsletter);
			}
		});

		form.addEventListener("submit", function (event) {
			event.preventDefault();

			if (busy || form.classList.contains("is-done")) {
				return;
			}

			var invalid = validate();
			if (invalid.length) {
				setStatus(status, "", "");
				invalid[0].focus();
				return;
			}

			if (!startedAt) {
				startedAt = Math.floor(Date.now() / 1000);
			}

			setBusy(true);
			setStatus(status, "loading", text("loading"));

			var controller = typeof AbortController === "function" ? new AbortController() : null;
			var timer = controller
				? setTimeout(function () {
						controller.abort();
					}, TIMEOUT_MS)
				: 0;

			var payload = JSON.stringify({
				email: email.value.trim(),
				newsletter: true,
				marketing: !!(marketing && marketing.checked),
				hp: honeypot ? honeypot.value : "",
				started_at: startedAt,
				source: source,
			});

			// Inside a promise, so a fetch wrapper that throws at once also ends
			// in .catch() and never leaves the form busy.
			Promise.resolve()
				.then(function () {
					return fetch(endpoint, {
						method: "POST",
						signal: controller ? controller.signal : undefined,
						// A public route: no session cookie goes with a sign-up.
						credentials: "omit",
						headers: { "Content-Type": "application/json", Accept: "application/json" },
						body: payload,
					});
				})
				.then(function (response) {
					return response.json().then(
						function (body) {
							return body;
						},
						function () {
							return null;
						}
					);
				})
				.then(function (body) {
					clearTimeout(timer);
					if (body && body.ok === true) {
						succeed();
						return;
					}
					fail(body && typeof body.code === "string" ? body.code : "error");
				})
				.catch(function () {
					// Network failure, abort (timeout) or a fetch that throws.
					clearTimeout(timer);
					fail("error");
				});
		});
	}

	function boot() {
		var forms = document.querySelectorAll("form[data-tvk-newsletter]");
		for (var i = 0; i < forms.length; i++) {
			init(forms[i]);
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", boot);
	} else {
		boot();
	}
})();
